import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { usePhoneManagementCtx } from '../PhoneManagement.context'
import {
  SetStateAction,
  Dispatch,
  useEffect,
  useMemo,
  ReactNode,
  useState,
  useCallback,
} from 'react'
import {
  buildTimeOptionsAmPm,
  DAY_ORDER,
  parseHmsToSec,
  safeParseMethods,
} from '@utils/phoneManagement.utils'
import {
  IClinicNumber,
  IWorkingHours,
  IWorkingHoursDataGrid,
} from '@dataTypes/clinic.type'
import { PhoneEditorState } from '../usePhoneManagement'
import { WEEKDAYS } from '@constants/Settings'
import { useAppDispatch } from '@store/hooks'
import { updateClinicNumberAction } from '@store/actions/settings-communication.action'
import { areValuesEqual } from '@utils/settings.util'

type WorkingHoursRowLike = {
  status: boolean
  day_of_week: string
  start_time: string
  end_time: string
}

type WhFieldError = { startInvalid: boolean; endInvalid: boolean }

interface IEditPhoneNumberHooks {
  cancelToList: () => void
  discardUnsavedChanges: () => Promise<void>
  extensionNumberError: string | null
  handleExtensionNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  hasUnsavedChanges: () => boolean
  hasWorkingHoursRangeError: boolean
  lastWorkingHoursTimeEdit: 'start_time' | 'end_time' | null
  onClickCancel: () => Promise<void>
  onHide: () => Promise<void>
  phoneEditor: PhoneEditorState | null
  rowFor: (
    d: IWorkingHours['day_of_week'],
  ) =>
    | Pick<IWorkingHours, 'day_of_week' | 'start_time' | 'end_time'>
    | undefined
  save: () => Promise<void>
  selectedPhoneLine: IClinicNumber | null
  setDraft: (p: Partial<IClinicNumber>) => void
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  setLastWorkingHoursTimeEdit: Dispatch<
    SetStateAction<'start_time' | 'end_time' | null>
  >
  setPhoneEditor: Dispatch<SetStateAction<PhoneEditorState | null>>
  setShowUnsavedChangesModal: Dispatch<SetStateAction<boolean>>
  showUnsavedChangesModal: boolean
  timeOpts: string[]
  toggleMethod: (m: string) => void
  updateWorkingHours: (day_of_week: string, key: string, value: string) => void
  workingHours: IWorkingHoursDataGrid[]
  workingHoursErrorMessage: string
  workingHoursFieldErrors: Map<string, WhFieldError>
}

const useEditPhoneNumber = (): IEditPhoneNumberHooks => {
  const hooks = usePhoneManagementCtx()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const dispatch = useAppDispatch()

  const [workingHours, setWorkingHours] = useState<IWorkingHoursDataGrid[]>([])
  const [lastWorkingHoursTimeEdit, setLastWorkingHoursTimeEdit] = useState<
    'start_time' | 'end_time' | null
  >(null)
  const [extensionNumberError, setExtensionNumberError] = useState<
    string | null
  >(null)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)

  const {
    selectedPhoneLine,
    phoneEditor,
    setPhoneEditor,
    cancelToList,
    runAsync,
  } = hooks

  const timeOpts = useMemo(() => buildTimeOptionsAmPm(30), [])

  const setDraft = (p: Partial<IClinicNumber>): void => {
    setPhoneEditor((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e))
  }

  const wh = phoneEditor?.workingHours || []

  const rowFor = (
    d: IWorkingHours['day_of_week'],
  ):
    | Pick<IWorkingHours, 'day_of_week' | 'start_time' | 'end_time'>
    | undefined => wh.find((x) => x.day_of_week === d)

  const toggleMethod = (m: string): void => {
    const cur = safeParseMethods(phoneEditor?.draft.contact_methods || '')
    const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]
    setDraft({ contact_methods: JSON.stringify(next) })
  }

  const weekdayBlockEnabled = WEEKDAYS.some((d) => !!rowFor(d))

  const handleExtensionNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value
    if (value.trim() === '') {
      setExtensionNumberError(null)
    } else if (!/^\d+$/.test(value)) {
      setExtensionNumberError('Only digits are allowed.')
    } else if (value.length < 4 || value.length > 4) {
      setExtensionNumberError(`Enter four digits (1000 - 9000).`)
    } else {
      setExtensionNumberError(null)
    }
    setDraft({ extension_number: value, extension_group: value })
  }

  const save = useCallback(async (): Promise<void> => {
    const _data = phoneEditor
    if (!_data?.draft.clinic_number_id) return

    cancelToList()

    await runAsync(
      () =>
        dispatch(
          updateClinicNumberAction({
            id: _data.draft.clinic_number_id!,
            data: {
              ..._data.draft,
              workingHours: _data.workingHours,
            },
          }),
        ).unwrap(),
      {
        successTitle: 'Phone line saved',
        successMessage: 'Your changes were saved successfully.',
        errorTitle: 'Could not save phone line',
        errorFallbackMessage: 'Unable to save changes. Please try again.',
      },
    )
  }, [phoneEditor, dispatch, runAsync, cancelToList])

  const updateWorkingHours = (
    day_of_week: string,
    key: string,
    value: string,
  ): void => {
    const index = workingHours.findIndex((x) => x.day_of_week === day_of_week)
    if (index !== -1) {
      let cleanValue: string | boolean = value
      if (key === 'status') {
        cleanValue = value === 'true'
      }
      const _workingHours = [...workingHours]
      _workingHours[index] = {
        ..._workingHours[index],
        [key]: cleanValue,
      }
      setWorkingHours(_workingHours)

      const toSaveInDatabase: IWorkingHours[] = phoneEditor?.draft
        .same_working_hours
        ? [
            ...(_workingHours[0].status
              ? WEEKDAYS.map((d) => ({
                  entity_type: 'clinic_number' as const,
                  entity_id: phoneEditor?.draft.clinic_number_id ?? '',
                  day_of_week: d,
                  start_time: _workingHours[0].start_time || '08:00:00',
                  end_time: _workingHours[0].end_time || '17:00:00',
                }))
              : []),
            ...(_workingHours[1].status
              ? [
                  {
                    entity_type: 'clinic_number' as const,
                    entity_id: phoneEditor?.draft.clinic_number_id ?? '',
                    day_of_week: 'Saturday' as const,
                    start_time: _workingHours[1].start_time || '08:00:00',
                    end_time: _workingHours[1].end_time || '17:00:00',
                  },
                ]
              : []),
            ...(_workingHours[2].status
              ? [
                  {
                    entity_type: 'clinic_number' as const,
                    entity_id: phoneEditor?.draft.clinic_number_id ?? '',
                    day_of_week: 'Sunday' as const,
                    start_time: _workingHours[2].start_time || '08:00:00',
                    end_time: _workingHours[2].end_time || '17:00:00',
                  },
                ]
              : []),
          ]
        : _workingHours
            .filter((x) => x.status)
            .map((x) => ({
              entity_type: 'clinic_number' as const,
              entity_id: phoneEditor?.draft.clinic_number_id ?? '',
              day_of_week: x.day_of_week as
                | 'Monday'
                | 'Tuesday'
                | 'Wednesday'
                | 'Thursday'
                | 'Friday'
                | 'Saturday'
                | 'Sunday',
              start_time: x.start_time || '08:00:00',
              end_time: x.end_time || '17:00:00',
            }))
      setPhoneEditor((e) => (e ? { ...e, workingHours: toSaveInDatabase } : e))
    }
  }

  useEffect(() => {
    if (phoneEditor?.draft.same_working_hours) {
      const monTime = phoneEditor?.workingHours?.find(
        (x) => x.day_of_week === 'Monday',
      )
      const satTime = phoneEditor?.workingHours?.find(
        (x) => x.day_of_week === 'Saturday',
      )
      const sunTime = phoneEditor?.workingHours?.find(
        (x) => x.day_of_week === 'Sunday',
      )
      setWorkingHours([
        {
          status: weekdayBlockEnabled,
          day_of_week: 'Weekdays (Monday → Friday)',
          start_time: monTime?.start_time || '08:00:00',
          end_time: monTime?.end_time || '17:00:00',
        },
        {
          status: !!satTime,
          day_of_week: 'Saturday',
          start_time: satTime?.start_time || '08:00:00',
          end_time: satTime?.end_time || '17:00:00',
        },
        {
          status: !!sunTime,
          day_of_week: 'Sunday',
          start_time: sunTime?.start_time || '08:00:00',
          end_time: sunTime?.end_time || '17:00:00',
        },
      ])
    } else {
      setWorkingHours(
        DAY_ORDER.map((day) => {
          const r = rowFor(day)
          return {
            status: !!r,
            day_of_week: day,
            start_time: r?.start_time || '08:00:00',
            end_time: r?.end_time || '17:00:00',
          }
        }),
      )
    }
  }, [phoneEditor])

  const hasWorkingTimeValue = (hms: string | undefined): boolean => {
    return typeof hms === 'string' && hms.trim().length > 0
  }

  const buildWorkingHoursFieldErrors = (
    rows: WorkingHoursRowLike[],
  ): Map<string, WhFieldError> => {
    const m = new Map<string, WhFieldError>()
    for (const row of rows) {
      if (!row.status) continue
      if (
        !hasWorkingTimeValue(row.start_time) ||
        !hasWorkingTimeValue(row.end_time)
      ) {
        continue
      }
      const startSec = parseHmsToSec(row.start_time)
      const endSec = parseHmsToSec(row.end_time)
      if (startSec !== null && endSec !== null && startSec > endSec) {
        m.set(row.day_of_week, {
          startInvalid: true,
          endInvalid: true,
        })
      }
    }
    return m
  }

  const workingHoursFieldErrors = useMemo(
    () => buildWorkingHoursFieldErrors(workingHours),
    [workingHours],
  )

  const hasWorkingHoursRangeError = workingHoursFieldErrors.size > 0
  useEffect(() => {
    if (!hasWorkingHoursRangeError) {
      setLastWorkingHoursTimeEdit(null)
    }
  }, [hasWorkingHoursRangeError])

  const workingHoursErrorMessage =
    lastWorkingHoursTimeEdit === 'end_time'
      ? 'Closing time cannot be earlier than opening time.'
      : 'Opening time cannot be later than closing time.'

  const hasUnsavedChanges = useCallback((): boolean => {
    if (!phoneEditor || !selectedPhoneLine) return false
    if (
      !areValuesEqual(
        [
          phoneEditor?.draft.is_main as boolean,
          phoneEditor?.draft.line_name as string,
          phoneEditor?.draft.contact_methods as string,
          phoneEditor?.draft.routed_to_type as string,
          phoneEditor?.draft.routed_to_value as string,
          phoneEditor?.draft.extension_number as string,
          phoneEditor?.draft.functions_send_sms as boolean,
          phoneEditor?.draft.functions_outbound_calls as boolean,
          phoneEditor?.draft.apply_working_hours as boolean,
          phoneEditor?.draft.same_working_hours as boolean,
        ],
        [
          selectedPhoneLine?.is_main as boolean,
          selectedPhoneLine?.line_name as string,
          selectedPhoneLine?.contact_methods as string,
          selectedPhoneLine?.routed_to_type as string,
          selectedPhoneLine?.routed_to_value as string,
          selectedPhoneLine?.extension_number as string,
          selectedPhoneLine?.functions_send_sms as boolean,
          selectedPhoneLine?.functions_outbound_calls as boolean,
          selectedPhoneLine?.apply_working_hours as boolean,
          selectedPhoneLine?.same_working_hours as boolean,
        ],
      )
    )
      return true
    return false
  }, [phoneEditor, selectedPhoneLine])

  const onClickCancel = useCallback(async (): Promise<void> => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesModal(true)
    } else {
      cancelToList()
    }
  }, [hasUnsavedChanges, setShowUnsavedChangesModal, cancelToList])

  const discardUnsavedChanges = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
    cancelToList()
  }, [setShowUnsavedChangesModal, cancelToList])

  const onHide = useCallback(async (): Promise<void> => {
    setShowUnsavedChangesModal(false)
  }, [setShowUnsavedChangesModal])

  return {
    cancelToList,
    extensionNumberError,
    hasUnsavedChanges,
    discardUnsavedChanges,
    handleExtensionNumberChange,
    hasWorkingHoursRangeError,
    lastWorkingHoursTimeEdit,
    onClickCancel,
    onHide,
    phoneEditor,
    rowFor,
    save,
    selectedPhoneLine,
    setDraft,
    setHeaderButtons,
    setHeaderTitle,
    setLastWorkingHoursTimeEdit,
    setPhoneEditor,
    setShowUnsavedChangesModal,
    showUnsavedChangesModal,
    timeOpts,
    toggleMethod,
    updateWorkingHours,
    workingHours,
    workingHoursErrorMessage,
    workingHoursFieldErrors,
  }
}

export default useEditPhoneNumber
